(() => {
  if (window.TaillaiDisclosureMotion) return;
  const attached = new WeakMap();
  const controls = new WeakMap();
  const mounted = new Map();
  const attach = (details, options = {}) => {
    if (!details || details.tagName !== 'DETAILS') return () => {};
    if (attached.has(details)) return attached.get(details);
    const summary = [...details.children].find(child => child.tagName === 'SUMMARY');
    if (!summary) return () => {};
    const controller = new AbortController();
    const signal = controller.signal;
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const properties = ['height', 'overflow', 'box-sizing'];
    const savedStyles = properties.map(name => [name, details.style.getPropertyValue(name), details.style.getPropertyPriority(name)]);
    const savedExpanded = summary.getAttribute('aria-expanded');
    const savedMotionOpen = details.getAttribute('data-motion-open');
    const content = [...details.children].filter(child => child !== summary);
    const savedInert = content.map(child => [child, child.getAttribute('inert')]);
    const restoreInert = () => savedInert.forEach(([child, value]) => {
      if (value === null) child.removeAttribute('inert');
      else child.setAttribute('inert', value);
    });
    let desiredOpen = details.open;
    let expectedOpen = details.open;
    let animation = null;
    let deadline = 0;
    let deadlineTimer = 0;
    let generation = 0;
    let destroyed = false;
    const nativeState = open => {
      expectedOpen = open;
      details.open = open;
    };
    const state = open => {
      details.dataset.motionOpen = String(open);
      summary.setAttribute('aria-expanded', String(open));
      if (open) restoreInert();
      else content.forEach(child => child.setAttribute('inert', ''));
    };
    const restoreStyles = () => savedStyles.forEach(([name, value, priority]) => {
      if (value) details.style.setProperty(name, value, priority);
      else details.style.removeProperty(name);
    });
    const cancel = () => {
      generation += 1;
      animation?.cancel();
      animation = null;
    };
    const settle = () => {
      clearTimeout(deadlineTimer);
      cancel();
      nativeState(desiredOpen);
      state(desiredOpen);
      restoreStyles();
    };
    const animateToTarget = (startHeight, duration) => {
      restoreStyles();
      nativeState(desiredOpen);
      const endHeight = details.getBoundingClientRect().height;
      if (reduced.matches || !details.isConnected || !details.animate || duration <= 0 || Math.abs(endHeight - startHeight) < 1) {
        settle();
        return;
      }
      nativeState(true);
      details.style.setProperty('box-sizing', 'border-box');
      details.style.setProperty('overflow', 'hidden');
      details.style.setProperty('height', `${startHeight}px`);
      const token = generation;
      animation = details.animate(
        [{ height: `${startHeight}px` }, { height: `${endHeight}px` }],
        { duration, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'both' }
      );
      animation.finished.then(() => {
        if (!destroyed && generation === token) settle();
      }).catch(() => {});
    };
    const update = open => {
      if (destroyed) return;
      const startHeight = details.getBoundingClientRect().height;
      cancel();
      clearTimeout(deadlineTimer);
      desiredOpen = open;
      if (!open && details.contains(document.activeElement) && !summary.contains(document.activeElement)) summary.focus({ preventScroll: true });
      state(open);
      options.onChange?.(open);
      const duration = open ? 280 : 220;
      deadline = performance.now() + duration;
      deadlineTimer = setTimeout(settle, duration);
      animateToTarget(startHeight, duration);
    };
    state(desiredOpen);
    summary.addEventListener('click', event => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
      const interactive = event.target.closest?.('a,button,input,select,textarea');
      if (interactive && interactive !== summary) return;
      event.preventDefault();
      update(!desiredOpen);
    }, { signal });
    details.addEventListener('toggle', () => {
      if (details.open === expectedOpen) return;
      desiredOpen = details.open;
      options.onChange?.(desiredOpen);
      settle();
    }, { signal });
    reduced.addEventListener('change', settle, { signal });
    addEventListener('resize', () => { if (animation) settle(); }, { signal });
    const contentResize = typeof ResizeObserver === 'function' ? new ResizeObserver(() => {
      if (!animation || destroyed) return;
      const startHeight = details.getBoundingClientRect().height;
      const remaining = deadline - performance.now();
      cancel();
      animateToTarget(startHeight, remaining);
    }) : null;
    content.forEach(child => contentResize?.observe(child));
    const destroy = () => {
      if (destroyed) return;
      settle();
      destroyed = true;
      controller.abort();
      contentResize?.disconnect();
      restoreInert();
      if (savedExpanded === null) summary.removeAttribute('aria-expanded');
      else summary.setAttribute('aria-expanded', savedExpanded);
      if (savedMotionOpen === null) details.removeAttribute('data-motion-open');
      else details.setAttribute('data-motion-open', savedMotionOpen);
      attached.delete(details);
      controls.delete(details);
    };
    attached.set(details, destroy);
    controls.set(details, (open, animate) => {
      if (animate) {
        if (open !== desiredOpen) update(open);
        return;
      }
      const changed = desiredOpen !== open;
      desiredOpen = open;
      if (!open && details.contains(document.activeElement) && !summary.contains(document.activeElement)) summary.focus({ preventScroll: true });
      state(open);
      if (changed) options.onChange?.(open);
      settle();
    });
    return destroy;
  };
  const setOpen = (details, open, { animate = true } = {}) => {
    const control = controls.get(details);
    if (control) control(Boolean(open), animate);
    else if (details?.tagName === 'DETAILS') details.open = Boolean(open);
  };
  const mount = root => {
    mounted.forEach((destroy, details) => {
      if (!details.isConnected) {
        destroy();
        mounted.delete(details);
      }
    });
    const details = [...root.querySelectorAll('[data-motion-disclosure]')];
    if (root.matches?.('[data-motion-disclosure]')) details.unshift(root);
    details.forEach(item => {
      if (!mounted.has(item)) mounted.set(item, attach(item));
    });
  };
  const unmount = root => mounted.forEach((destroy, details) => {
    if (root === document || root === details || root.contains(details) || !details.isConnected) {
      destroy();
      mounted.delete(details);
    }
  });
  window.TaillaiDisclosureMotion = { attach, setOpen, mount, unmount };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(document), { once: true });
  else mount(document);
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => unmount(event.target));
  addEventListener('pagehide', () => unmount(document));
  addEventListener('pageshow', event => { if (event.persisted) mount(document); });
})();
